/**
 * Tool catalogue. Tools are declared once here so the tool rail, the status bar
 * and the shortcut layer stay in sync. `plannedFor` marks tools that are listed
 * but not wired yet — the rail shows them disabled instead of hiding features
 * that are coming.
 */

export type ToolId =
  | 'move'
  | 'brush'
  | 'pencil'
  | 'eraser'
  | 'line'
  | 'rect'
  | 'ellipse'
  | 'arrow'
  | 'text'
  | 'fill'
  | 'eyedropper'
  | 'crop'
  | 'zoom';

export interface ToolDef {
  id: ToolId;
  label: string;
  /** Single-key shortcut, matched outside text fields. */
  key: string;
  /** Iteration in which the tool becomes functional. */
  plannedFor?: number;
  hint: string;
}

export const TOOLS: readonly ToolDef[] = [
  { id: 'move', label: 'Move / Select', key: 'v', hint: 'Select and transform layers' },
  { id: 'crop', label: 'Crop', key: 'c', hint: 'Select a region, then crop to it' },
  { id: 'brush', label: 'Brush', key: 'b', hint: 'Soft-edged painting' },
  { id: 'pencil', label: 'Pencil', key: 'n', hint: 'Hard-edged pixel painting' },
  { id: 'eraser', label: 'Eraser', key: 'e', hint: 'Remove paint from a layer' },
  { id: 'line', label: 'Line', key: 'l', hint: 'Draw a straight line' },
  { id: 'rect', label: 'Rectangle', key: 'r', hint: 'Draw a rectangle' },
  { id: 'ellipse', label: 'Ellipse', key: 'o', hint: 'Draw an ellipse' },
  { id: 'arrow', label: 'Arrow', key: 'a', hint: 'Draw an arrow' },
  { id: 'text', label: 'Text', key: 't', hint: 'Add and edit text' },
  { id: 'fill', label: 'Fill', key: 'g', hint: 'Flood-fill a region' },
  { id: 'eyedropper', label: 'Eyedropper', key: 'i', hint: 'Pick a colour' },
  { id: 'zoom', label: 'Zoom', key: 'z', hint: 'Zoom in and out' },
] as const;

export const TOOL_BY_ID: Record<ToolId, ToolDef> = Object.fromEntries(
  TOOLS.map((tool) => [tool.id, tool]),
) as Record<ToolId, ToolDef>;

export function toolForKey(key: string): ToolDef | undefined {
  return TOOLS.find((tool) => tool.key === key.toLowerCase());
}

export function isToolPlanned(id: ToolId): boolean {
  return TOOL_BY_ID[id]?.plannedFor !== undefined;
}
